import { ApplicationRef, ComponentRef, DOCUMENT, EnvironmentInjector, Injectable, createComponent, inject } from '@angular/core';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { SidePanelStackBarComponent } from './side-panel-stack-bar.component';

/** The panelClass every right-docked side panel carries (openSingletonDialog's callers, COL-746/973). */
export const SIDE_PANEL_CLASS = 'record-detail-sidebar-dialog';

/** On a panel's overlay host while a panel opened from it is showing. */
export const SIDE_PANEL_HIDDEN_CLASS = 'side-panel-stack--hidden';
/** On a panel's overlay pane while it is showing over another one (it carries the Back strip). */
export const SIDE_PANEL_CHILD_CLASS = 'side-panel-stack--child';
/** On a hidden panel's backdrop that stays, because the panel over it has none of its own. */
const BACKDROP_KEPT_CLASS = 'side-panel-stack--backdrop-kept';
const ENTER_CLASS = 'side-panel-stack--enter';
const REVEAL_CLASS = 'side-panel-stack--reveal';

/** A panel's own close (✕) button — the one that, in a nested panel, closes them all. */
const CLOSE_BUTTON_SELECTOR = '[data-side-panel-close], .detail-close, button[aria-label="Close"]';

/** What a panel showing over another is called in the strip, by its panelClass. */
const PANEL_KINDS: readonly (readonly [string, string])[] = [
  ['inspection-form-sidebar-dialog', 'Inspection'],
  ['action-form-sidebar-dialog', 'Action'],
  ['wo-preview-sidebar-dialog', 'Work order'],
  ['work-order-form-sidebar-dialog', 'Work order'],
  ['work-package-form-sidebar-dialog', 'Work package'],
  ['bog-form-sidebar-dialog', 'Boots on ground'],
  ['booking-detail-sidebar-dialog', 'Booking'],
  ['eoi-detail-sidebar-dialog', 'EOI'],
  ['schedule-detail-sidebar-dialog', 'Schedule'],
];

interface StackEntry {
  ref: MatDialogRef<unknown>;
  pane: HTMLElement;
  /** The overlay's outermost element (hidden as a whole). */
  host: HTMLElement;
  backdrop: HTMLElement | null;
  /** Where focus last was inside this panel — returned to when Back brings it back. */
  lastFocused: HTMLElement | null;
  bar: ComponentRef<SidePanelStackBarComponent> | null;
  /** The panel's own ✕ was used: closing it closes the panels underneath too. */
  closeAll: boolean;
  /** Back is running the panel's own close: its ✕ click is not "close all". */
  backPending: boolean;
  /** Back / Esc brought this one back: focus returns into it once the panel over it has gone. */
  restoreFocusTo: StackEntry | null;
  listeners: (() => void)[];
}

/**
 * Side panels opened from inside side panels drill in rather than stack (COL-973): the panel opened
 * takes the right-docked slot, the one it was opened from is hidden — kept alive, so Back returns to
 * it exactly as it was (open sections, scroll, form state) — and only one is ever on screen.
 *
 * Every right-docked panel takes part without its opener doing anything: this listens to every
 * MatDialog open (MatDialog.afterOpened reaches the root one from any injector) and acts on dialogs
 * carrying SIDE_PANEL_CLASS. Confirm dialogs, pickers and menus don't carry it, so they open over a
 * panel as before and hide nothing.
 *
 * - A panel opened while another is showing hides that one (its overlay, and its backdrop when the
 *   new one brings its own) and gets a strip above its header: Back and the chain of panels.
 * - Back (the strip, or Esc, or the panel's Cancel) closes the top panel the way its own ✕ does —
 *   running its unsaved-changes handling — and shows the one underneath again; focus returns where
 *   it was. Whatever the closed panel's opener subscribed to (afterClosed, saved$) still runs.
 * - The top panel's own ✕ closes the whole stack, as does leaving for a page ("Open full page":
 *   a close result with `fullPage` / `navigated`, or a route change while panels are nested).
 */
@Injectable({ providedIn: 'root' })
export class SidePanelStackService {
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly appRef = inject(ApplicationRef);
  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly document = inject(DOCUMENT);

  private readonly entries: StackEntry[] = [];
  private started = false;

  /** Starts watching dialogs (once, from an app initializer). */
  init(): void {
    if (this.started) return;
    this.started = true;
    this.dialog.afterOpened.subscribe((ref) => this.onOpened(ref));
    let path = this.pathOf(this.router.url);
    this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe((event) => {
      const next = this.pathOf(event.urlAfterRedirects);
      // A nested panel that left for a page leaves nothing behind it.
      if (next !== path && this.entries.length > 1) this.closeAll();
      path = next;
    });
  }

  /** How many panels are hidden under the one showing (0: a single panel, or none). */
  get depth(): number {
    return Math.max(0, this.entries.length - 1);
  }

  /** Whether `ref` is showing over another side panel. */
  isNested(ref: MatDialogRef<unknown>): boolean {
    return this.entries.findIndex((entry) => entry.ref === ref) > 0;
  }

  /**
   * Back from the panel showing (or `ref`): closes it the way its own ✕ would — so its own close
   * logic (unsaved changes, the result it reports) runs — without closing the panels underneath.
   * False when there is nothing to go back to.
   */
  back(ref?: MatDialogRef<unknown>): boolean {
    const entry = ref ? this.entries.find((e) => e.ref === ref) : this.entries.at(-1);
    if (!entry || this.entries.indexOf(entry) === 0) return false;
    const closeButton = this.closeButtonOf(entry);
    entry.backPending = true;
    try {
      if (closeButton) closeButton.click();
      else entry.ref.close();
    } finally {
      entry.backPending = false;
    }
    return true;
  }

  /** Closes every side panel in the stack, top first. */
  closeAll(): void {
    const all = this.entries.splice(0).reverse();
    for (const entry of all) {
      this.release(entry);
      if (entry.ref.getState() === MatDialogState.OPEN) entry.ref.close();
    }
  }

  // ── Opening ─────────────────────────────────────────────────────────────────────────────────────

  private onOpened(ref: MatDialogRef<unknown>): void {
    const pane = this.paneOf(ref);
    if (!pane?.classList.contains(SIDE_PANEL_CLASS)) return;
    const host = this.hostOf(pane);
    const entry: StackEntry = {
      ref, pane, host,
      backdrop: this.backdropOf(host),
      lastFocused: null,
      bar: null,
      closeAll: false,
      backPending: false,
      restoreFocusTo: null,
      listeners: [],
    };
    const parent = this.showingTop();
    this.entries.push(entry);
    this.watch(entry);

    if (parent) {
      this.hide(parent, entry);
      this.attachBar(entry);
      this.animate(pane, ENTER_CLASS);
      // The trigger was in the panel now hidden: start in this one, at Back — unless the panel's
      // own autoFocus already put focus inside it.
      ref.afterOpened().subscribe(() => {
        if (!pane.contains(this.document.activeElement)) entry.bar?.instance.focusBack();
      });
    }

    ref.beforeClosed().subscribe((result) => this.onClosing(entry, result));
    ref.afterClosed().subscribe(() => this.onClosed(entry));
  }

  /** The panel on screen: the newest one not already closing. */
  private showingTop(): StackEntry | null {
    for (let i = this.entries.length - 1; i >= 0; i--) {
      if (this.entries[i].ref.getState() === MatDialogState.OPEN) return this.entries[i];
    }
    return null;
  }

  private watch(entry: StackEntry): void {
    const listen = <K extends keyof HTMLElementEventMap>(type: K, handler: (event: HTMLElementEventMap[K]) => void) => {
      entry.pane.addEventListener(type, handler, true);
      entry.listeners.push(() => entry.pane.removeEventListener(type, handler, true));
    };
    listen('focusin', (event) => {
      const target = event.target as HTMLElement | null;
      if (target && !entry.bar?.location.nativeElement.contains(target)) entry.lastFocused = target;
    });
    // ✕ marks "close them all"; anything else done in the panel since (a Stay on an unsaved-changes
    // prompt, then Save) clears it again, so only a close that ✕ actually led to closes the stack.
    listen('click', (event) => {
      if (!entry.backPending && this.isCloseButton(entry, event.target)) entry.closeAll = true;
    });
    const clear = (event: Event) => {
      if (!this.isCloseButton(entry, event.target)) entry.closeAll = false;
    };
    listen('pointerdown', clear);
    listen('keydown', clear);
    // A click on a kept backdrop is a click outside every panel: when it closes the panel, all go.
    const backdropClicks = entry.ref.backdropClick().subscribe(() => {
      entry.closeAll = true;
    });
    entry.listeners.push(() => backdropClicks.unsubscribe());
  }

  // ── Closing ─────────────────────────────────────────────────────────────────────────────────────

  private onClosing(entry: StackEntry, result: unknown): void {
    const index = this.entries.indexOf(entry);
    if (index < 0) return;
    const wasTop = index === this.entries.length - 1;
    this.entries.splice(index, 1);
    this.release(entry);

    if (entry.closeAll || this.isLeaving(result)) {
      this.closeAll();
      return;
    }
    const top = this.entries.at(-1);
    if (!top) return;
    if (wasTop) {
      // Shown again now, not once the closing one has animated out: focus can only return to a
      // visible panel, and MatDialog restores it as soon as the closing one is gone.
      this.reveal(top);
      entry.restoreFocusTo = top;
    }
    this.refreshChrome();
  }

  private onClosed(entry: StackEntry): void {
    const target = entry.restoreFocusTo;
    // Gone, or hidden again by a panel that replaced the closed one (openSingletonDialog).
    if (!target || !this.entries.includes(target) || target.host.classList.contains(SIDE_PANEL_HIDDEN_CLASS)) return;
    const active = this.document.activeElement;
    if (active && active !== this.document.body && target.pane.contains(active)) return;
    const back = target.lastFocused?.isConnected && target.pane.contains(target.lastFocused) ? target.lastFocused : null;
    (back ?? target.pane.querySelector<HTMLElement>('.mat-mdc-dialog-container'))?.focus({ preventScroll: true });
  }

  /** A close result that says the panel left for a page. */
  private isLeaving(result: unknown): boolean {
    if (!result || typeof result !== 'object') return false;
    const r = result as { fullPage?: unknown; navigated?: unknown };
    return r.fullPage === true || r.navigated === true;
  }

  private release(entry: StackEntry): void {
    entry.listeners.splice(0).forEach((remove) => remove());
    this.removeBar(entry);
  }

  private removeBar(entry: StackEntry): void {
    if (entry.bar) {
      this.appRef.detachView(entry.bar.hostView);
      entry.bar.destroy();
      entry.bar = null;
    }
    entry.pane.classList.remove(SIDE_PANEL_CHILD_CLASS);
  }

  // ── Showing and hiding ──────────────────────────────────────────────────────────────────────────

  private hide(parent: StackEntry, child: StackEntry): void {
    parent.host.classList.add(SIDE_PANEL_HIDDEN_CLASS);
    parent.pane.inert = true;
    if (parent.backdrop) {
      // One dimmed layer at a time: the parent's backdrop goes when the child brings its own, and
      // stays (outside the hidden host when the overlay is a popover) when it doesn't.
      const childHasBackdrop = !!child.backdrop;
      parent.backdrop.classList.toggle(SIDE_PANEL_HIDDEN_CLASS, childHasBackdrop);
      parent.backdrop.classList.toggle(BACKDROP_KEPT_CLASS, !childHasBackdrop);
    }
  }

  private reveal(entry: StackEntry): void {
    entry.host.classList.remove(SIDE_PANEL_HIDDEN_CLASS);
    entry.pane.inert = false;
    entry.backdrop?.classList.remove(SIDE_PANEL_HIDDEN_CLASS, BACKDROP_KEPT_CLASS);
    this.animate(entry.pane, REVEAL_CLASS);
  }

  private animate(pane: HTMLElement, cls: string): void {
    pane.classList.remove(ENTER_CLASS, REVEAL_CLASS);
    pane.classList.add(cls);
    const done = () => pane.classList.remove(cls);
    pane.addEventListener('animationend', done, { once: true });
    setTimeout(done, 400);
  }

  /** The strip on the top panel, and its chain — after any panel in the stack comes or goes. */
  private refreshChrome(): void {
    this.entries.forEach((entry, i) => {
      if (i === 0) {
        this.removeBar(entry);
        return;
      }
      if (!entry.bar) this.attachBar(entry);
      else this.updateBar(entry);
    });
  }

  private attachBar(entry: StackEntry): void {
    const bar = createComponent(SidePanelStackBarComponent, { environmentInjector: this.environmentInjector });
    this.appRef.attachView(bar.hostView);
    bar.instance.back.subscribe(() => this.back(entry.ref));
    entry.pane.insertBefore(bar.location.nativeElement, entry.pane.firstChild);
    entry.pane.classList.add(SIDE_PANEL_CHILD_CLASS);
    entry.bar = bar;
    this.updateBar(entry);
  }

  private updateBar(entry: StackEntry): void {
    if (!entry.bar) return;
    const index = this.entries.indexOf(entry);
    entry.bar.setInput('ancestors', this.entries.slice(0, index).map((e) => this.titleOf(e)));
    entry.bar.setInput('current', this.kindOf(entry) ?? this.titleOf(entry));
    entry.bar.changeDetectorRef.detectChanges();
  }

  // ── The DOM a MatDialog leaves ──────────────────────────────────────────────────────────────────

  private paneOf(ref: MatDialogRef<unknown>): HTMLElement | null {
    const fromComponent = (ref.componentRef?.location.nativeElement as HTMLElement | undefined)?.closest?.('.cdk-overlay-pane');
    return (fromComponent as HTMLElement | null) ?? this.document.getElementById(ref.id)?.closest<HTMLElement>('.cdk-overlay-pane') ?? null;
  }

  private hostOf(pane: HTMLElement): HTMLElement {
    return pane.parentElement ?? pane;
  }

  /** The overlay's backdrop: inside its host (popover overlays) or just before it. */
  private backdropOf(host: HTMLElement): HTMLElement | null {
    const inside = Array.from(host.children).find((el) => el.classList.contains('cdk-overlay-backdrop'));
    if (inside) return inside as HTMLElement;
    const before = host.previousElementSibling;
    return before?.classList.contains('cdk-overlay-backdrop') ? (before as HTMLElement) : null;
  }

  private containerOf(entry: StackEntry): HTMLElement {
    return entry.pane.querySelector<HTMLElement>('.mat-mdc-dialog-container') ?? entry.pane;
  }

  private closeButtonOf(entry: StackEntry): HTMLElement | null {
    return this.containerOf(entry).querySelector<HTMLElement>(CLOSE_BUTTON_SELECTOR);
  }

  private isCloseButton(entry: StackEntry, target: EventTarget | null): boolean {
    if (!(target instanceof Element)) return false;
    const button = target.closest(CLOSE_BUTTON_SELECTOR);
    return !!button && this.containerOf(entry).contains(button);
  }

  /** A panel's name for the strip: its heading as shown, else what kind of panel it is. */
  private titleOf(entry: StackEntry): string {
    const heading = this.containerOf(entry).querySelector('h1, h2, h3, [role="heading"]');
    const text = heading?.textContent?.replace(/\s+/g, ' ').trim();
    return text || this.kindOf(entry) || 'Details';
  }

  private kindOf(entry: StackEntry): string | null {
    return PANEL_KINDS.find(([cls]) => entry.pane.classList.contains(cls))?.[1] ?? null;
  }

  private pathOf(url: string): string {
    return url.split(/[?#]/)[0];
  }
}
